/**
 * Client-side reCAPTCHA Enterprise helper.
 *
 * - Lazy-loads the Enterprise script once per page using the canonical public
 *   site key from `@/lib/recaptcha-site-key`.

 * - Returns a fresh score-based token bound to a per-form `action` name on
 *   each call. Tokens are short-lived; always fetch one immediately before
 *   submitting the enquiry, never up-front.
 */
import { RECAPTCHA_SITE_KEY } from "@/lib/recaptcha-site-key";

type EnterpriseGrecaptcha = {
  ready: (cb: () => void) => void;
  execute: (siteKey: string, options: { action: string }) => Promise<string>;
};

declare global {
  interface Window {
    grecaptcha?: { enterprise?: EnterpriseGrecaptcha };
  }
}

let scriptPromise: Promise<void> | null = null;

async function loadSiteKey(): Promise<string> {
  return RECAPTCHA_SITE_KEY;
}


async function loadScript(siteKey: string): Promise<void> {
  if (typeof window === "undefined") return;
  if (window.grecaptcha?.enterprise) return;
  if (!scriptPromise) {
    scriptPromise = new Promise((resolve, reject) => {
      const existing = document.querySelector<HTMLScriptElement>(
        'script[data-recaptcha-enterprise="1"]',
      );
      if (existing) {
        existing.addEventListener("load", () => resolve());
        existing.addEventListener("error", () => reject(new Error("recaptcha load failed")));
        return;
      }
      const s = document.createElement("script");
      s.src = `https://www.google.com/recaptcha/enterprise.js?render=${encodeURIComponent(siteKey)}`;
      s.async = true;
      s.defer = true;
      s.dataset.recaptchaEnterprise = "1";
      s.onload = () => resolve();
      s.onerror = () => reject(new Error("recaptcha load failed"));
      document.head.appendChild(s);
    });
  }
  await scriptPromise;
}

/**
 * Fetch a fresh reCAPTCHA Enterprise token for the given action.
 * Returns an empty string when reCAPTCHA is unavailable (script blocked by
 * ad-blocker, offline, missing site key). The server treats an empty token
 * as an infrastructure failure and still accepts the lead — better than
 * throwing "Could not verify you're human" at a real customer.
 */
export async function getRecaptchaToken(action: string): Promise<string> {
  if (typeof window === "undefined") return "";
  try {
    const siteKey = await loadSiteKey();
    if (!siteKey) return "";
    await loadScript(siteKey);
    const ge = window.grecaptcha?.enterprise;
    if (!ge) return "";
    await new Promise<void>((resolve) => ge.ready(resolve));
    return await ge.execute(siteKey, { action });
  } catch (err) {
    console.warn("recaptcha unavailable", err instanceof Error ? err.message : err);
    return "";
  }
}
